import { Mesh, Object3D } from 'three';
import { ArticlesCreator } from '@project/components/article/presenter/article-creator';
import { NumberUtils } from '@shared/util/number-utils';
import { ArticleUsageInfoPropertyDef } from '@article/constants/article-usage-info-property-def';
import { ArticleBehaviorPropertyDef } from '@article/constants/article-behavior-property-def';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { BaseContainer3D } from '@3d-scene/model3d/base-container-3d';
import { ArticleUtils } from '@article/util/article-utils';
import { Object3DUtils } from '@3d-scene/common/util/object-3d-utils';
import { Connection } from '@3d-scene/model3d/connection';
import { ConnectionAreaVO, MetaElementVO } from '@shared/vo/meta-element.vo';
import { UnitConverter } from '@3d-scene/common/util/unit-converter';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { Face3D } from '@3d-scene/model/face-3d';
import { ArrayUtils } from '@shared/util/array-utils';
import { MathUtils } from '@3d-scene/common/util/math-utils';
import { Dictionary } from 'typescript-collections';
import { ConnectionInfoUtils } from '@3d-scene/model3d/util/connection-info-utils';
import { IsThreeJsType } from '@3d-scene/common/util/is-threejs-type';

export class MountingRailArticlePostParseProcessor {
  public static readonly MAX_LENGTH: number = 20000;
  public static readonly MAX_FREEMOUTNING_LENGTH: number = 80000;

  public static canModify3DArticleGeometry(): boolean {
    return true;
  }

  public static process(
    articleContainer: ArticleContainer,
    articleVO: ArticleVO,
    containerService: ContainerService,
    connectionInfoUtils: ConnectionInfoUtils
  ): void {
    if (articleContainer.articleObject3DDataShared) {
      throw new Error('Modifying a shared article geometry is not allowed!');
    }
    const articleUsage: ArticleUsageVO = articleContainer.articleUsage;
    let articleLength = ArticleUsageUtils.getArticleUsageInfoPropertyFloatValue(articleUsage, ArticleUsageInfoPropertyDef.ARTICLE_LENGTH);
    if (!NumberUtils.isNumber(articleLength)) {
      articleLength = this.getRailMaxLength(articleContainer.article);
      ArticleUsageUtils.setArticleUsageInfoPropertyFloatValue(articleUsage, ArticleUsageInfoPropertyDef.ARTICLE_LENGTH, articleLength);
    }

    const connections: Dictionary<string, MetaElementVO> = ArticleUtils.getMetaElementsAsMap(articleVO);
    this.calculateMountingRailSize(articleContainer.model, articleUsage, connections, containerService);
    if (articleContainer instanceof BaseContainer3D) {
      const connectionNames: string[] = [];
      for (const connection of articleContainer.connections) {
        connectionNames.push(connection.id);
      }
      this.changeMeshes(articleContainer, articleContainer.model, connectionNames, articleLength, connectionInfoUtils);
    }
  }

  public static getRailMaxLength(article: ArticleVO | undefined): number {
    const maxLength = ArticleUtils.getArticleBehaviorPropertyNumberValue(article, ArticleBehaviorPropertyDef.MAX_LENGTH);
    if (!NumberUtils.isNumber(maxLength) || maxLength === 0) {
      // stub value to stretch mounting rail
      return this.MAX_LENGTH;
    } else {
      return UnitConverter.fromTo('mm', '3d', maxLength);
    }
  }

  private static changeMeshes(
    articleContainer: ArticleContainer,
    model: Object3D,
    connectionNames: string[],
    extrusionLength: number,
    connectionInfoUtils: ConnectionInfoUtils
  ): void {
    const children: Object3D[] = Object3DUtils.getChildrenRecursively(model);
    for (const child of children) {
      this.changeMeshes(articleContainer, child, connectionNames, extrusionLength, connectionInfoUtils);
      if (IsThreeJsType.mesh(child)) {
        if (connectionNames.indexOf(child.name) === -1) {
          // if mounting rail
          const extrusion: Mesh = ArticlesCreator.createDynamicArticle(child, extrusionLength);
          model.remove(child);
          Object3DUtils.dispose(child);
          model.add(extrusion);
        } else {
          // if connection
          const connection: Connection = articleContainer.findConnectionByName(child.name);
          const meta: MetaElementVO = connection.metaElementVO;
          if (meta && meta instanceof ConnectionAreaVO) {
            const connectionMesh: Mesh = ArticlesCreator.createRangeOnLineConnection(child, meta, connection, extrusionLength);
            if (connectionMesh) {
              model.remove(child);
              Object3DUtils.destroy(child);
              model.add(connectionMesh);
            }
          }
        }
      }
    }
  }

  private static calculateMountingRailSize(
    mountingRailContainer: Object3D,
    mountingRailUsage: ArticleUsageVO,
    metaElementsMap: Dictionary<string, MetaElementVO>,
    containerService: ContainerService
  ): void {
    if (!mountingRailContainer || !mountingRailUsage) {
      return;
    }
    const connectionNames: any[] = [];
    for (const metaElementName of metaElementsMap.values()) {
      connectionNames.push(metaElementName);
    }
    const mountingRailFaces: Face3D[] = containerService.extractFaces(mountingRailContainer, connectionNames);
    if (ArrayUtils.isEmpty(mountingRailFaces)) {
      return;
    }

    let bottomPosition: number = Number.MAX_VALUE;
    let minBottomDepth: number = Number.MAX_VALUE;
    let maxBottomDepth: number = -Number.MAX_VALUE;

    let topPosition: number = -Number.MAX_VALUE;
    let minTopDepth: number = Number.MAX_VALUE;
    let maxTopDepth: number = -Number.MAX_VALUE;

    for (const face of mountingRailFaces) {
      for (const vertex of face.vertices) {
        if (MathUtils.equalsNumber(vertex.y, bottomPosition)) {
          if (vertex.z > maxBottomDepth) {
            maxBottomDepth = vertex.z;
          }
          if (vertex.z < minBottomDepth) {
            minBottomDepth = vertex.z;
          }
        } else if (vertex.y < bottomPosition) {
          bottomPosition = vertex.y;
          minBottomDepth = maxBottomDepth = vertex.z;
        }

        if (MathUtils.equalsNumber(vertex.y, topPosition)) {
          if (vertex.z > maxTopDepth) {
            maxTopDepth = vertex.z;
          }
          if (vertex.z < minTopDepth) {
            minTopDepth = vertex.z;
          }
        } else if (vertex.y > topPosition) {
          topPosition = vertex.y;
          minTopDepth = maxTopDepth = vertex.z;
        }
      }
    }

    ArticleUsageUtils.setArticleUsageInfoPropertyFloatValue(
      mountingRailUsage,
      ArticleUsageInfoPropertyDef.BOTTOM_ARTICLE_DEPTH,
      maxBottomDepth - minBottomDepth,
      true
    );
    ArticleUsageUtils.setArticleUsageInfoPropertyFloatValue(
      mountingRailUsage,
      ArticleUsageInfoPropertyDef.TOP_ARTICLE_DEPTH,
      maxTopDepth - minTopDepth,
      true
    );
  }
}
